import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import { GetStaticProps } from 'next';
import { ParsedUrlQuery } from 'querystring';
import { useRouter } from 'next/router';

/* Apis */
import { skus as API } from '@api/apis';

/* Components */
import AddSku from '@scenes/AddSkuPage';
import { getLayout } from '@components/PageLayouts/Layout/Standard';
import LoaderPages from '@components/Loaders/Loader.pages';

/* Constants/Utils */
import { PAGE_TYPE_ADD_SKU } from '@constants/pages';
import { REMOTE_QUESTION } from '@scenes/AddSkuPage/addsku.constants';
import { SEVEN_DAYS } from '@constants/CacheRevalidate';
import { findCustomPartnerAndMerge } from '@integrations/partners/partners.utils';
import { LAYOUT_PAGE_TYPES } from '@components/PageLayouts/pagelayouts.constants';

/* Types */
import { SkuPageContainerType } from 'types/sku.types';
import { Children } from 'types/base.types';
// import { RouteSkuLinks, RouteSkuParams } from 'types/routes.types';

/*
 * Our base  Sku Page. The page proper is broken out just in case we ever need to
 * house that container in another layout.
 * */
function AddSkuPage(props: Required<SkuPageContainerType>): Children {
  const router = useRouter();

  if (router.isFallback) {
    return <LoaderPages message="Generating Questions Page. Thank you!" />;
  }

  return <AddSku {...props} />;
}

AddSkuPage.pageAttributes = { page: PAGE_TYPE_ADD_SKU };
AddSkuPage.Layout = getLayout({ promo: false, footer: false, checkout: true, page: PAGE_TYPE_ADD_SKU });

export default AddSkuPage;

/**
 * ---------------------------------------------------------------------------------------
 * Static Generation
 * @param params
 * ---------------------------------------------------------------------------------------
 */
interface StaticParams extends ParsedUrlQuery {
  id: string;
}

export const getStaticProps: GetStaticProps = async context => {
  const params = context.params as StaticParams;
  const { id, seoName } = params;
  const { data: { questions = '' } = {} } = await API.questions({ id });
  const { data: { sku = '' } = {} } = await API.find({ id });

  /* If errors */
  if (!sku) {
    throw new Error(`Cannot find add sku: '${seoName}'`);
  }

  /* ----------- PARTNER INTEGRATION ----------- */
  /* Lets merge our passed partner with a potentional custom/default partner */
  const partnerSkuData = findCustomPartnerAndMerge(sku.partner, { mergedAttributes: { page: LAYOUT_PAGE_TYPES.PRECART } });

  /* Lets merge our sku data since its SSR into our sku partner. So we can get the benefit of having all partner stuff upfront */
  if (partnerSkuData) {
    sku.partner = partnerSkuData;
  }
  /* ----------- END PARTNER INTEGRATION ----------- */

  // Here we will merge remote hellotech now into questions if appropriate
  if (questions && sku) {
    if (sku.remote && !sku.remoteOnly) {
      if (!find(questions, { id: REMOTE_QUESTION.id })) {
        questions.push(cloneDeep(REMOTE_QUESTION));
      }
    }
  }

  return {
    props: {
      sku,
      questions,
      partner: partnerSkuData,
    },
    revalidate: SEVEN_DAYS,
  };
};

/**
 * -------------------------------------------------------------------------
 * Static Path Generation
 * @param params
 */
export async function getStaticPaths<GetStaticPaths>() {
  /* Get Navigatable Service Skus */
  // const {data} = await API.all();

  /* Pluck out just the seoName for pathing purposes for routes */
  // const allSkusIds = data?.routes?.service_categories.reduce((skuRoutes: RouteSkuParams<string>[], item: RouteSkuLinks) => {
  //   item.skus.forEach(({id}) => {
  //     skuRoutes.push({
  //       params: {
  //         id: String(id),
  //       },
  //     });
  //   });
  //
  //   return skuRoutes;
  // }, []);

  return {
    paths: [],
    fallback: 'blocking',
  };
}
